<template>
  <div class="google-pay-button-wrapper" :lang="googlePayLang">
    <button
      type="button"
      class="googlepay long"
      :class="{ submitting: submitting }"
      :disabled="disabled || submitting"
      :aria-label="ariaLabel"
      @click="handleClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  disabled?: boolean
  submitting?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  submitting: false,
})

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void
}>()

const { t, i18n } = useI18n()

/**
 * Maps Alfred locale codes to Google Pay supported language codes.
 * Google Pay provides localized "Buy with" buttons for these languages:
 * bg, cs, de, es, fr, hr, id, ja, ko, ms, nl, pl, pt, ru, sk, sl, sr, tr, uk, zh
 *
 * This automatically handles all regional variants (e.g., fr-BE, fr-CA, fr-CH)
 * by extracting the base language code. Unsupported languages fall back to English.
 */
const googlePayLang = computed(() => {
  const currentLocale = i18n.locale
  const langCode = currentLocale.split('-')[0]

  // Languages supported by Google Pay button assets in googlepay-black.css
  const supportedLangs = new Set([
    'bg',
    'cs',
    'de',
    'es',
    'fr',
    'hr',
    'id',
    'ja',
    'ko',
    'ms',
    'nl',
    'pl',
    'pt',
    'ru',
    'sk',
    'sl',
    'sr',
    'tr',
    'uk',
    'zh',
  ])

  return supportedLangs.has(langCode) ? langCode : 'en'
})

const ariaLabel = computed(() => {
  return props.submitting ? t('Processing…') : t('Book now')
})

function handleClick(event: MouseEvent) {
  if (!props.disabled && !props.submitting) {
    emit('click', event)
  }
}
</script>

<style scoped>
/**
 * Google Pay Button Styles
 * Using official Google Pay button CSS with embedded SVG data URIs
 * Source: https://developers.google.com/pay/api/web/guides/brand-guidelines
 */

/* Import Google Pay CSS co-located with component */
@import './googlepay-black.css';

.google-pay-button-wrapper {
  width: 100%;
  display: block;
}

/**
 * Override Google Pay default sizing to match our BaseSubmitButton
 * - Full width (asblock)
 * - Match minimum height (48px)
 * - Match border radius
 */
.googlepay {
  width: 100% !important;
  min-width: 100% !important;
  min-height: 48px !important;
  height: 48px !important;
  border-radius: 0.5em;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/**
 * Disabled state
 * Match BaseSubmitButton disabled styling
 */
.googlepay:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

/**
 * Submitting state
 * Reduce opacity to indicate processing
 */
.googlepay.submitting {
  opacity: 0.7;
  cursor: wait;
  pointer-events: none;
}
</style>
